"use client";

import { Button } from "@src/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@src/components/ui/dialog";
import { Input } from "@src/components/ui/input";
import { Label } from "@src/components/ui/label";
import { useCallback, useState } from "react";
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useInsertPlaylist } from "@src/mutations/useInsertPlaylist";
import { useAuth } from "../../_components/providers/auth-provider";
import { NotAuthenticatedError } from "@src/errors";
import { reportError } from "@lib/utils/reportError";
import { LoadingSpinner } from "@src/components/tw/loading-spinner";
import { toast } from "sonner";
import { ReactNode } from "react";
import { useRouter } from "next/navigation";
import { paths } from "@src/utils/paths";
import { posthog, AnalyticsEvent } from "@src/analytics";
import { useSidebar } from "@src/components/ui/sidebar";
import { nameSchema, MAX_NAME_LENGTH } from "@src/validation/schemas";
interface NewPlaylistDialogProps {
  trigger: ReactNode;
  onSuccess?: (playlistId: string) => void;
}
export default function NewPlaylistDialog({
  trigger,
  onSuccess
}: NewPlaylistDialogProps) {
  const [open, setOpen] = useState(false);
  const [playlistName, setPlaylistName] = useState("");
  const {
    mutate,
    status,
    reset
  } = useInsertPlaylist();
  const {
    user
  } = useAuth();
  const router = useRouter();
  const {
    setOpenMobile: setSidebarMobileOpen
  } = useSidebar();
  const [error, setError] = useState<string | null>(null);
  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setPlaylistName("");
      setError(null);
    } else {
      reset();
    }
  }, [reset]);
  const handleCreatePlaylist = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      if (!user) {
        throw new NotAuthenticatedError("User not authenticated");
      }
      const result = nameSchema.safeParse(playlistName);
      if (!result.success) {
        setError(result.error.issues[0].message);
        return;
      }
      setError(null);
      mutate({
        playlist: {
          userId: user.id,
          name: playlistName
        }
      }, {
        onSuccess: data => {
          setOpen(false);
          toast.success("Playlist created successfully");
          posthog.capture(AnalyticsEvent.PLAYLIST_CREATED, {
            playlistId: data.id,
            playlistName
          });
          if (onSuccess) {
            onSuccess(data.id);
          }

          // Make sure sidebar closes, in case this was created from the sidebar
          // TODO: Ideally this would happen elsewhere - something listening for the current
          // path, and closing the sidebar on navigation or something
          setSidebarMobileOpen(false);
          router.push(paths.playlist(data.id));
        },
        onError: () => {
          toast.error("Failed to create playlist");
        }
      });
    } catch (error) {
      reportError(error);
      toast.error("Something went wrong. Please try again.");
    }
  }, [user, mutate, playlistName, onSuccess, router, setSidebarMobileOpen]);
  return <Dialog open={open} onOpenChange={handleOpenChange} data-sentry-element="Dialog" data-sentry-component="NewPlaylistDialog" data-sentry-source-file="NewPlaylistDialog.tsx">
      <DialogTrigger asChild data-sentry-element="DialogTrigger" data-sentry-source-file="NewPlaylistDialog.tsx">
        {trigger}
      </DialogTrigger>
      <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="NewPlaylistDialog.tsx">
        <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="NewPlaylistDialog.tsx">
          <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="NewPlaylistDialog.tsx">Create new playlist</DialogTitle>
          <VisuallyHidden data-sentry-element="VisuallyHidden" data-sentry-source-file="NewPlaylistDialog.tsx">
            <DialogDescription data-sentry-element="DialogDescription" data-sentry-source-file="NewPlaylistDialog.tsx">
              Create a new playlist
            </DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <form className="grid gap-8 py-4" onSubmit={handleCreatePlaylist}>
          <div className="grid gap-2">
            <Label htmlFor="playlist-name" data-sentry-element="Label" data-sentry-source-file="NewPlaylistDialog.tsx">Playlist name</Label>
            <Input id="playlist-name" value={playlistName} onChange={e => setPlaylistName(e.target.value)} placeholder="My Awesome Playlist" maxLength={MAX_NAME_LENGTH} data-sentry-element="Input" data-sentry-source-file="NewPlaylistDialog.tsx" />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <Button type="submit" disabled={status === "pending"} data-ph-capture-attribute-action="playlist-create-submit" data-sentry-element="Button" data-sentry-source-file="NewPlaylistDialog.tsx">
            {status === "pending" ? <LoadingSpinner className="size-4" /> : "Create Playlist"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>;
}